import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { galleryData as fallbackGalleryData } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GallerySection = ({ t }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [galleryData, setGalleryData] = useState(fallbackGalleryData);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${API_URL}/api/gallery`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Transform API data to match expected format
            const transformedData = data.map(item => ({
              id: item.id,
              before: item.before_image,
              after: item.after_image,
              type: item.item_type,
              description: item.description
            }));
            setGalleryData(transformedData);
          }
        }
      } catch (error) {
        console.log('Using fallback gallery data');
      }
    };

    fetchGallery();
  }, []);

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % galleryData.length);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + galleryData.length) % galleryData.length);
  };

  if (galleryData.length === 0) {
    return null;
  }

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0d2e5d] mb-4">
            {t.gallery.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.gallery.subtitle}
          </p>
        </div>

        {/* Gallery Carousel */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Before Image */}
            <div className="relative group">
              <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-red-500 text-white font-bold rounded-lg shadow-lg">
                {t.gallery.before}
              </div>
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={galleryData[activeIndex].before}
                  alt="Before cleaning"
                  className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* After Image */}
            <div className="relative group">
              <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-[#5cd26f] text-white font-bold rounded-lg shadow-lg">
                {t.gallery.after}
              </div>
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={galleryData[activeIndex].after}
                  alt="After cleaning"
                  className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              onClick={handlePrev}
              variant="outline"
              size="icon"
              className="w-12 h-12 rounded-full border-2 border-[#2196f3] hover:bg-[#2196f3] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>

            {/* Dots Indicator */}
            <div className="flex gap-2">
              {galleryData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeIndex
                      ? 'bg-[#2196f3] w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={handleNext}
              variant="outline"
              size="icon"
              className="w-12 h-12 rounded-full border-2 border-[#2196f3] hover:bg-[#2196f3] hover:text-white transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Description */}
          <div className="text-center mt-6">
            <p className="text-gray-600 italic">
              {galleryData[activeIndex].description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
